<template>
  <div class="admin-product-list">
    <h2>회원 정보 조회하기</h2>
    <div class="button-box">
      <button @click="router.push('/admin/consumer/add')">
        회원 정보 등록하기
      </button>
      <button @click="syncConsumerGrade">회원 등급 동기화하기</button>
    </div>
    <div class="table-box">
      <h3>상품 목록 테이블</h3>
      <div ref="tableRef"></div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import "frappe-datatable/dist/frappe-datatable.min.css";
import DataTable from "frappe-datatable";
import {
  collection,
  query,
  orderBy,
  getDocs,
  where,
  Timestamp,
  writeBatch,
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import { convertPriceToUserGradeCode, formatDate } from "@/lib/utils";
import router from "@/router";
import { useBusyLock } from "@/lib/useBusyLock";

const { runLocked } = useBusyLock();

const tableRef = ref(null);
const dataTable = ref(null);
const originData = ref([]);

const gradeOrder = [
  "N1",
  "N2",
  "N3",
  "N4",
  "N5",
  "N6",
  "N7",
  "N8",
  "N9",
  "N10",
]; // 필요하면 확장 가능

const normalizeGrade = (grade, minGrade) => {
  const gradeIdx = gradeOrder.indexOf(grade);
  const minGradeIdx = gradeOrder.indexOf(minGrade);

  // 예외값 방어 (gradeOrder에 없는 값이 들어온 경우)
  if (gradeIdx === -1) return minGrade;
  if (minGradeIdx === -1) return grade;

  return gradeIdx < minGradeIdx ? minGrade : grade;
};

const getSyncDateRange = (currentDate = new Date()) => {
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth() + 1;

  // 시작: 3개월 전 1일 00:00:00.000
  let startMonth = month - 3;
  let startYear = year;
  if (startMonth <= 0) {
    startMonth += 12;
    startYear -= 1;
  }
  const start = new Date(startYear, startMonth - 1, 1, 0, 0, 0, 0);

  // 끝: 직전 달 말일 23:59:59.999
  let endMonth = month - 1;
  let endYear = year;
  if (endMonth <= 0) {
    endMonth += 12;
    endYear -= 1;
  }
  const end = new Date(endYear, endMonth, 0, 23, 59, 59, 999);

  return {
    start: Timestamp.fromDate(start),
    end: Timestamp.fromDate(end),
    periodLabel: `${startYear}년 ${startMonth}월 ~ ${endYear}년 ${endMonth}월`,
  };
};

const syncConsumerGrade = () =>
  runLocked(async () => {
    const currentDate = new Date();
    const { start, end, periodLabel } = getSyncDateRange(currentDate);

    if (!confirm(`${periodLabel} 기준으로 등급 동기화를 진행하시겠습니까?`)) {
      return;
    }

    const usersSnap = await getDocs(collection(db, "users"));

    // Firestore writeBatch는 최대 500건 제한이 있어서 분할 커밋 처리
    let batch = writeBatch(db);
    let writeCount = 0;
    let updatedCount = 0;

    for (const userDoc of usersSnap.docs) {
      const userData = userDoc.data();

      // 3개월 기준 구매확정 주문 조회
      const productOrdersSnap = await getDocs(
        query(
          collection(db, "productOrder"),
          where("userId", "==", userData.userId),
          where("status", "==", "CONFIRM_PURCHASE"),
          where("createdAt", ">=", start),
          where("createdAt", "<=", end)
        )
      );

      // 총 구매금액 계산
      const totalPrice = productOrdersSnap.docs.reduce(
        (acc, d) => acc + Number(d.data().productPrice || 0),
        0
      );

      // 등급 계산 + 최소등급 보정
      const calculatedGrade = convertPriceToUserGradeCode(totalPrice);
      const finalGrade = normalizeGrade(calculatedGrade, userData.userMinGrade);

      // 변경 필요할 때만 업데이트
      if (finalGrade !== userData.userGrade) {
        batch.update(userDoc.ref, {
          userGrade: finalGrade,
        });
        writeCount += 1;
        updatedCount += 1;

        // 500 제한 여유 두고 커밋
        if (writeCount >= 450) {
          await batch.commit();
          batch = writeBatch(db);
          writeCount = 0;
        }
      }
    }

    // 남은 배치 커밋
    if (writeCount > 0) {
      await batch.commit();
    }

    alert(`회원 등급 동기화가 완료되었습니다! (변경 ${updatedCount}명)`);
    window.location.reload();
  }).catch((error) => {
    console.error("Error syncing consumer grade:", error);
    alert("회원 등급 동기화에 실패하였습니다!");
  });

onMounted(() => {
  runLocked(async () => {
    // 1) Firebase에서 데이터 가져오기
    const q = query(collection(db, "users"), orderBy("createdAt", "desc"));
    const querySnapshot = await getDocs(q);

    // 2) 문서 배열 변환 (원본 저장)
    originData.value = querySnapshot.docs.map((docSnap) => ({
      ...docSnap.data(),
      id: docSnap.id,
    }));

    const data = querySnapshot.docs.map((docSnap) => {
      const item = docSnap.data();

      return [
        {
          content: `<a href="${
            window.location.origin
          }/admin/consumer/detail?id=${
            docSnap.id
          }" style="color: #007bff; font-weight: 700">${
            item.userPhone || "-"
          }</a>`,
          editable: false,
        },
        {
          content: item.userName || "-",
          editable: false,
        },
        {
          content: item.userBirthday || "-",
          editable: false,
        },
        {
          content: item.userMinGrade || "-",
          editable: false,
        },
        {
          content: item.userGrade || "-",
          editable: false,
        },
        {
          content: item.isAdmin ? "관리자" : "일반 회원",
          editable: false,
        },
        {
          content: item.userSalespersonCode || "-",
          editable: false,
        },
        {
          content: item.visitedAt?.toDate
            ? formatDate(item.visitedAt.toDate())
            : "-",
          editable: false,
        },
        {
          content: item.createdAt?.toDate
            ? formatDate(item.createdAt.toDate())
            : "-",
          editable: false,
        },
      ];
    });

    // (선택) 기존 datatable 제거 후 재생성
    if (dataTable.value) {
      try {
        dataTable.value.destroy();
      } catch (e) {
        console.warn("기존 DataTable destroy 실패(무시 가능):", e);
      }
      dataTable.value = null;
    }

    // 3) frappe-datatable 생성
    dataTable.value = new DataTable(tableRef.value, {
      columns: [
        {
          name: "휴대폰 번호",
          editable: false,
          resizable: false,
          width: 128,
          align: "center",
        },
        {
          name: "이름",
          editable: false,
          resizable: false,
          width: 72,
          align: "center",
        },
        {
          name: "생일",
          editable: false,
          resizable: false,
          width: 128,
          align: "center",
        },
        {
          name: "최소 회원 등급",
          editable: false,
          resizable: false,
          width: 128,
          align: "center",
        },
        {
          name: "현재 회원 등급",
          editable: false,
          resizable: false,
          width: 128,
          align: "center",
        },
        {
          name: "관리자 여부",
          editable: false,
          resizable: false,
          width: 96,
          align: "center",
        },
        {
          name: "영업자 코드",
          editable: false,
          resizable: false,
          width: 112,
          align: "center",
        },
        {
          name: "마지막 방문일자",
          editable: false,
          resizable: false,
          width: 180,
          align: "center",
        },
        {
          name: "가입일자",
          editable: false,
          resizable: false,
          width: 180,
          align: "center",
        },
      ],
      data,
      checkboxColumn: true,
      serialNoColumn: false,
      inlineFilters: true,
      cellHeight: 48,
    });
  }).catch((error) => {
    console.error("회원 목록 로딩 실패:", error);
    alert("회원 목록을 불러오는 중 오류가 발생했습니다.");
  });
});
</script>

<style scoped lang="scss">
.admin-product-list {
  margin-top: 36px;

  > .button-box {
    margin-top: 24px;
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;

    > button {
      padding: 8px 16px;
      border-radius: 4px;
      border: none;
      font-weight: 500;
      cursor: pointer;
      font-size: 14px;

      &.red {
        background-color: #dc3545;
        color: white;

        &:hover {
          background-color: #c82333;
        }
      }

      &.blue {
        background-color: #007bff;
        color: white;

        &:hover {
          background-color: #0056b3;
        }
      }

      &.secondary {
        background-color: #6c757d;
        color: white;

        &:hover {
          background-color: #545b62;
        }
      }
    }
  }

  > .table-box {
    margin-top: 24px;
    box-shadow: 8px 8px 16px rgba(0, 0, 0, 0.25);
    border-radius: 8px;
    padding: 24px;

    > div {
      margin-top: 16px;
      width: 100%;
      height: fit-content;
    }
  }

  > dialog {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background-color: #fff;
    border-radius: 8px;
    width: 90%;
    height: 90%;
    max-width: 600px;
    box-shadow: 8px 8px 16px rgba(0, 0, 0, 0.25);

    &::backdrop {
      background: rgba(0, 0, 0, 0.5);
    }

    > header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 8px 16px;
      position: sticky;
      top: 0;
      border-bottom: 1px solid rgba(0, 0, 0, 0.25);
      background: #efefef;

      > h2 {
        font-size: 18px;
      }

      > button {
        border: none;
        border-radius: 4px;
        padding: 4px 8px;
        background-color: #007bff;
        color: #fff;
        font-weight: 700;
        cursor: pointer;

        > span {
          font-size: 16px;
        }
      }
    }

    > main {
      padding: 16px;
      > textarea {
        width: 100%;
        height: 300px;
        padding: 8px;
        border-radius: 4px;
        border: 1px solid #ccc;
      }

      button {
        border: none;
        border-radius: 4px;
        padding: 6px 12px;
        background-color: #007bff;
        color: #fff;
        font-weight: 500;
        cursor: pointer;
        font-size: 14px;
      }
    }
  }
}
</style>
